import { createApi } from '@reduxjs/toolkit/query/react';
import { cloneDeep } from 'lodash';
import type { Key } from 'react';

import type { DataUpdatedProps } from '../../components/WidgetSettingWindow/widget-settings-dialog';
import { CHART_TYPE } from '../../constants/enums';
import { getStorageItem } from '../../helpers/storage';
import { baseQueryWithReAuth } from './api';
import type { BasicDataChartResponse } from './chart-data';
import { detailsWindowApi } from './details-window';

// Re-export with Dto suffix for naming consistency
export type { BasicDataChartResponse as BasicChartDataDto } from './chart-data';

import type { LayoutWidget } from './layout';
import { layoutWidgetApi } from './layout-widget';
import { tableDataApi } from './table-data';

//TODO: Determine which fields are required and if these are all of the settings

export type Colors = Record<string, string>;

export interface KpiTargetConfiguration extends Record<string, Key | boolean | undefined | Record<string, number>> {
  hideKPI: boolean;
  lineColor?: string;
  caption: string;
  onFRAxis?: boolean;
  id: string;
  janValue?: number;
  febValue?: number;
  marValue?: number;
  aprValue?: number;
  mayValue?: number;
  julValue?: number;
  junValue?: number;
  augValue?: number;
  sepValue?: number;
  octValue?: number;
  novValue?: number;
  decValue?: number;
  value?: Record<string, number>;
}
export interface WidgetConfiguration {
  caseInsensitive: boolean;
  colors?: Colors;
  disableFilterOutNoHoursBranches: boolean;
  filterOutNoDataBranches: boolean;
  filterOutNoTargetPeople: boolean;
  gridCellBgColors: Colors;
  hideBlankCategory: boolean;
  ignoreDateRangeFilter: boolean;
  labelDecimals: number;
  labelFormat?: string;
  labelPlacement?: string;
  secondColorEnabled: boolean;
  sortOrder: Array<string>;
  showTRIFR: boolean;
  showTop?: string;
  toggle?: boolean;
  lineDisplayValue?: boolean;
  displayDecimalPlaces?: number;
  yScaleMin?: number | null;
  yScaleMax?: number | null;
  kpiTargetConfiguration?: KpiTargetConfiguration;
  calculationType?: string;
  names?: Record<string, string>;
  defaultColor?: string;
  secondColor?: string;
  threshold?: string;
  kpiTargetForColumnKpiPercentage: Record<string, string>;
  hierarchyForLocation?: string;
}

export interface RollingFR {
  title: string;
  module: string;
  form: string;
  field: string;
  values: string[];
  factor: number;
  displayValues: boolean;
  colors?: string[];
  ignoreOuterFilter: boolean;
  hours: {
    module: string;
    form: string;
    field: string;
  };
  legend?: string[];
}

export interface WidgetData {
  module: string;
  moduleViewId: string;
  form: string[];
  field: string;
  includeAverageColumn: boolean;
  fetchParent: boolean;
  fetchLinkedRecord: boolean;
  applyYTDColumn: boolean;
  threatColumnExist: boolean;
  displayLegend: boolean;
  ascendingSort: boolean;
  disableAutoSort: boolean;
  lineField: string;
  linePercentage?: {
    fieldValues: string[];
    seriesName: string;
  };
  lineType?: string;
  forms: string[];
  timeFrequency: string;
  secondField: string;
  customFilterField: string[];
  rollingTRIFR: RollingFR;
  rollingFR: RollingFR;
  rollingSR: RollingFR;
  kpiFieldValue: string;
  lineValues: number;
  visibleColumns?: Array<string>;
  pureVisibleColumns?: Array<string>;
  calculationType?: string;
  globalFilters?: Array<GlobalFiltersConfigs>;
  excludeArchivedRecords?: boolean;
  ignoreDateFilters?: boolean;
}

export interface GlobalFiltersConfigs {
  id: string;
  notBlank: boolean;
  values: Array<string>;
}

export interface WidgetConfigurationData {
  calculatedVisibleColumns: Array<string>;
  dataURL: string;
  id: string;
  title: string;
  type: CHART_TYPE;
  widgetConfig?: WidgetConfiguration;
  widgetData: WidgetData;
  fieldMapping: Record<string, unknown>;
  injuryFrequencyAIConfig?: {
    [key: string]: string | Array<string>;
  };
  hoursWorkedAIConfig?: {
    [key: string]: string | Array<string>;
  };
  widgetSubType?: 'STACKED_COLUMN' | 'SIDE_BY_SIDE_COLUMN' | 'GROUPED_BY_DATE_COLUMN';
}

export interface ColumnLineWidgetConfiguration {
  widgetConfig: WidgetConfiguration;
  widgetData: WidgetData;
}

export interface WidgetSimplifiedData {
  id: number;
  title: string;
  type: string;
  dataURL: string;
}

export type GetWidgetInsightsEnabledApiResponse = /** status 200 OK */ boolean;
export type GetWidgetInsightsEnabledApiArg = void;
export type GetInsightsApiResponse = /** status 200 OK */ WidgetInsightsResponseDto;
export type GetInsightsApiArg = {
  widgetId: string;
  widgetInsightsRequestBodyDto: WidgetInsightsRequestBodyDto;
};
export type WidgetInsightsResponseDto = {
  data_analysis?: string;
  potential_causes?: string;
  recommendations?: string;
  future_trends?: string;
  missing_values_analysis?: string;
  summary?: string;
};

export type WidgetInsightsRequestBodyDto = {
  data?: BasicDataChartResponse[];
  extraPrompt?: string;
};
export type StartRecordsInsightsAnalysisApiResponse = /** status 200 OK */ string;
export type StartRecordsInsightsAnalysisApiArg = {
  widgetId: string;
  from?: string;
  to?: string;
  extraPrompt?: string;
};
export type GetRecordsInsightsResultsApiResponse = /** status 200 OK */ RecordAnalysisJobResponseDto;
export type GetRecordsInsightsResultsApiArg = {
  analysisId: string;
};
export type RecordsAnalysisResultsDto = {
  data_analysis?: string;
  root_cause?: string;
  frequency_trends?: string;
  benchmarking?: string;
  recommendations?: string;
};
export type RecordAnalysisJobResponseDto = {
  state?: string;
  status?: string;
  task?: string;
  ai_model?: string;
  result?: RecordsAnalysisResultsDto;
  start_time?: string;
  end_time?: string;
  error?: Record<string, string>;
};

export type GetAllWidgetsSimplifiedApiArg = {
  isFormConfigurationRequired?: boolean;
};

export type StartWidgetsDataAnalysisApiResponse = /** status 200 OK */ string;

export type StartWidgetsDataAnalysisApiArg = {
  widgetId: string;
  widgetInsightsDataDto: WidgetInsightsDataDto;
};

export type GetWidgetsDataAnalysisResultApiResponse = /** status 200 OK */ WidgetDataAnalysisJobResponseDto;

export type GetWidgetsDataAnalysisResultApiArg = {
  analysisId: string;
};

export type WidgetInsightsDataDto = {
  data?: BasicDataChartResponse[];
  extraPrompt?: string;
};

export type WidgetDataAnalysisJobResponseDto = {
  status?: string;
  error?: {
    [key: string]: object;
  };
  task?: string;
  ai_model?: string;
  start_time?: string;
  end_time?: string;
  result?: WidgetInsightsResponseDto;
};

export const widgetConfigurationApi = createApi({
  reducerPath: 'widget',
  baseQuery: baseQueryWithReAuth,
  tagTypes: ['widgetConfig', 'widgetConfigSimplified'],
  endpoints: (builder) => ({
    widgetConfiguration: builder.query<WidgetConfigurationData, string | number>({
      query: (widgetId) => ({ url: `/widgets/${widgetId}`, method: 'GET' }),
      providesTags: (result, _error, id) =>
        result
          ? [{ type: 'widgetConfig' as const, id: typeof id === 'number' ? id : String(id) }, 'widgetConfig']
          : ['widgetConfig'],
    }),
    updateWidgetConfigurationData: builder.mutation<void, Partial<DataUpdatedProps>>({
      query: ({ id, ...body }) => ({
        url: `/widgets/${id}`,
        method: 'PATCH',
        body: body,
      }),
      invalidatesTags: (_result, _error, { id }) => {
        return id
          ? [
              { type: 'widgetConfig', id: id },
              { type: 'widgetConfigSimplified', id: id },
            ]
          : [];
      },
      async onQueryStarted({ id, ...body }, { dispatch, queryFulfilled }) {
        const activeTabId = getStorageItem('activeLayoutTabId', 'sessionStorage');
        try {
          dispatch(
            layoutWidgetApi.util.updateQueryData('getLayoutWidget', activeTabId, (draft) => {
              draft.forEach((widget, index) => {
                if (widget.widget.id === id) {
                  const thisWidget = cloneDeep(widget);
                  const newWidget = {
                    widget: {
                      ...thisWidget.widget,
                      widgetData: {
                        ...thisWidget.widget.widgetData,
                        ...body.widgetData,
                      },
                      widgetConfig: {
                        ...thisWidget.widget.widgetConfig,
                        ...body.widgetConfig,
                        kpiTargetConfiguration: {
                          ...thisWidget.widget.widgetConfig?.kpiTargetConfiguration,
                          ...body.widgetConfig?.kpiTargetConfiguration,
                        },
                      },
                      hoursWorkedAIConfig: {
                        ...thisWidget.widget.hoursWorkedAIConfig,
                        ...body.hoursWorkedAIConfig,
                      },
                      injuryFrequencyAIConfig: {
                        ...thisWidget.widget.injuryFrequencyAIConfig,
                        ...body.injuryFrequencyAIConfig,
                      },
                    },
                  };
                  draft[index] = { ...thisWidget, ...newWidget } as LayoutWidget;
                }
              });
            })
          );

          await queryFulfilled;
          //Table header settings must be refetched
          //as configuration for columns is stored there
          if (body.isTable) {
            dispatch(tableDataApi.util.invalidateTags([{ type: 'TableHeader', id: id } as const]));
            dispatch(tableDataApi.util.invalidateTags([{ type: 'RegularTableData', id: `LIST-${id}` } as const]));
          }
          //invalidate details' window header and data
          dispatch(detailsWindowApi.util.invalidateTags([{ type: 'DetailsHeader', id: id } as const]));
          dispatch(detailsWindowApi.util.invalidateTags([{ type: 'DetailsData', id: `LIST-${id}` } as const]));
        } catch (error) {
          console.log(error);
        }
      },
    }),
    addWidgetConfiguration: builder.mutation<void, Partial<WidgetConfigurationData>>({
      query: (body) => {
        return {
          url: `/widgets`,
          method: 'POST',
          body,
        };
      },
    }),
    widgetSimplifiedConfigurationData: builder.query<Array<WidgetSimplifiedData>, GetAllWidgetsSimplifiedApiArg>({
      query: (queryArg) => ({
        url: `/widgets/simplified/all`,
        method: 'GET',
        params: {
          isFormConfigurationRequired: Boolean(queryArg.isFormConfigurationRequired),
        },
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'widgetConfigSimplified' as const, id })), 'widgetConfigSimplified']
          : ['widgetConfigSimplified'],
      transformResponse: (response: Array<WidgetSimplifiedData>) => {
        return response.map((el) => ({
          ...el,
          text: `${el.title} (${el.type})`,
        }));
      },
    }),
    widgetsAllConfigurationData: builder.query<Array<WidgetConfigurationData>, unknown>({
      query: () => ({
        url: `/widgets/all`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'widgetConfig' as const, id })), 'widgetConfig']
          : ['widgetConfig'],
    }),
    deleteWidget: builder.mutation<void, number>({
      query: (widgetId) => ({ url: `/widgets/${widgetId}`, method: 'DELETE' }),
      invalidatesTags: (result, error, widgetId) => [
        { type: 'widgetConfig', id: typeof widgetId === 'number' ? widgetId : String(widgetId) },
      ],
    }),

    deleteLayoutWidget: builder.mutation<void, string>({
      query: (id) => ({
        url: `/layout-widgets/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'widgetConfig', id: typeof id === 'number' ? id : String(id) }],
    }),
    getWidgetInsightsEnabled: builder.query<GetWidgetInsightsEnabledApiResponse, GetWidgetInsightsEnabledApiArg>({
      query: () => ({ url: `/metadata/ai-insights-enabled` }),
    }),
    getInsights: builder.mutation<GetInsightsApiResponse, GetInsightsApiArg>({
      query: (queryArg) => ({
        url: `/widget-insights/data-analysis/${queryArg.widgetId}`,
        method: 'POST',
        body: queryArg.widgetInsightsRequestBodyDto,
      }),
    }),
    startRecordsInsightsAnalysis: builder.mutation<
      StartRecordsInsightsAnalysisApiResponse,
      StartRecordsInsightsAnalysisApiArg
    >({
      query: (queryArg) => ({
        url: `/widget-insights/injury-records-analysis/${queryArg.widgetId}/start`,
        method: 'POST',
        params: {
          from: queryArg['from'],
          to: queryArg.to,
          extraPrompt: queryArg.extraPrompt,
        },
      }),
    }),
    getRecordsInsightsResults: builder.query<GetRecordsInsightsResultsApiResponse, GetRecordsInsightsResultsApiArg>({
      query: (queryArg) => ({ url: `/widget-insights/injury-records-analysis/${queryArg.analysisId}/results` }),
    }),
    startWidgetsDataAnalysis: builder.mutation<StartWidgetsDataAnalysisApiResponse, StartWidgetsDataAnalysisApiArg>({
      query: (queryArg) => ({
        url: `/widget-insights/data-analysis/${queryArg.widgetId}/start`,
        method: 'POST',
        body: queryArg.widgetInsightsDataDto,
      }),
    }),
    getWidgetsDataAnalysisResult: builder.query<
      GetWidgetsDataAnalysisResultApiResponse,
      GetWidgetsDataAnalysisResultApiArg
    >({
      query: (queryArg) => ({ url: `/widget-insights/data-analysis/${queryArg.analysisId}/results` }),
    }),
  }),
});

export const {
  useWidgetConfigurationQuery,
  useUpdateWidgetConfigurationDataMutation,
  useAddWidgetConfigurationMutation,
  useWidgetSimplifiedConfigurationDataQuery,
  useWidgetsAllConfigurationDataQuery,
  useDeleteLayoutWidgetMutation,
  useDeleteWidgetMutation,
  useGetWidgetInsightsEnabledQuery,
  useGetInsightsMutation,
  useStartRecordsInsightsAnalysisMutation,
  useGetRecordsInsightsResultsQuery,
  useStartWidgetsDataAnalysisMutation,
  useGetWidgetsDataAnalysisResultQuery,
} = widgetConfigurationApi;
